import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import { gql } from 'apollo-angular';
import moment from 'moment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { AnalyticsEventIdentifyProperties } from 'src/app/core/services/analytics/models/analytics-event-identify-properties';
import {
  ADD_CARD_STEP,
  ADD_ORGANIZATION_STEP,
  AUTHORISATION_FORM_STEP,
  DIRECT_DEBIT_STEP,
  FUNDING_INFO_STEP,
  FUNDING_ORGANISATION_STEP,
  HOME_CARE_PACKAGE_STEP,
  INVOICING_STEP,
  NDIS_NUMBER_STEP,
  PAYMENT_INFORMATION_STEP,
  POWER_OF_ATTORNEY_STEP,
  RECEIPTS_STEP,
  UPLOAD_AUTHORISATION_FORM_STEP,
  UPLOAD_NDIS_LETTER_STEP,
  VERIFY_PAYMENT_STEP,
} from 'src/app/pages/consumer-account/constants/payment-details-steps.constant';
import { Step } from 'src/app/pages/consumer-account/models/payment-step.model';
import { VoucherAttributes, VoucherResponse } from 'src/app/pages/consumer-account/models/voucher-attributes.model';
import { ConsumerAccountStatus } from 'src/app/shared/models/gql/consumer-account-status.model';
import { ConsumerAccount } from 'src/app/shared/models/gql/consumer-account.model';
import { Services } from 'src/app/shared/models/gql/services.model';
import { AccountService } from 'src/app/shared/services/account.service';
import { ApiInterfaceService } from 'src/app/shared/services/api-interface.service';
import { cloneStep } from 'src/app/shared/utilities/clone-step-shallow';
import { attemptLocalToInternationalMobileNumber } from 'src/app/shared/utilities/phone-number-format';
import { AccountVerificationComponent } from '../components/payment-details/components/account-verification/account-verification.component';
import { ContactUsComponent } from '../components/payment-details/components/contact-us/contact-us.component';
import { CreditCardConfirmationComponent } from '../components/payment-details/components/credit-card-confirmation/credit-card-confirmation.component';

import {
  about,
  behaviourSupportPlan,
  consumerNavigation,
  contacts,
  houseHold,
  jobPosted,
  paymentCreditCard,
  paymentDebitCard,
  paymentDetails,
  PaymentFundingInformation,
  paymentHomeCarePackageProvider,
  PaymentHowToVerify,
  PaymentInvoice,
  PaymentNdisnumber,
  PaymentUploadAuthorisationForm,
  personalDetails,
  personManagingAccount,
  personReceivingSupport,
  petsDetails,
  ScheduleCall,
  supportNeeds,
  supportTakePlace,
} from '../fragments/consumer-account-fragments';

@Injectable({
  providedIn: 'root',
})
export class ConsumerAccountService {
  allSteps = {
    personalDetailsSteps: [
      {
        name: 'personal details',
        link: '/client-account/personal-details/person-receiving-support',
        value: 'personReceivingSupport',
        isShown: true,
        status: false,
      }, // This name is getting override in getConsumerPersonalDetails() method
      {
        name: 'personal details',
        link: '/client-account/personal-details/person-managing-account',
        value: 'personManagingAccount',
        isShown: false,
        status: false,
      }, // This name is getting override in getConsumerPersonalDetails() method
      {
        name: 'Where will the support take place?',
        link: '/client-account/personal-details/support-take-place',
        isShown: true,
        value: 'supportTakePlace',
        status: false,
      },
      {
        name: 'Behaviour care plan',
        link: '/client-account/personal-details/behaviour-support-plan',
        value: 'behaviourSupportPlan',
        isShown: true,
        status: true,
      },
    ],

    supportNeedsSteps: [
      {
        name: 'Support needs',
        link: '/client-account/supportneeds-details/support-needs',
        value: 'supportNeeds',
        isShown: true,
        status: false,
      },
      {
        name: 'How to support',
        link: '/client-account/supportneeds-details/how-to-support',
        value: 'howToSupport',
        isShown: true,
        status: false,
      }, // This name is getting override in getConsumerPersonalDetails() method
      {
        name: 'Medication',
        link: '/client-account/supportneeds-details/medication',
        value: 'medication',
        isShown: true,
        status: false,
      },
      {
        name: 'Documents',
        link: '/client-account/supportneeds-details/documents',
        value: 'documents',
        isShown: true,
        status: false,
      },
    ],
  };

  personalDetailsSteps: any[] = [];

  supportNeedsSteps: any[] = [];

  paymentDetailsSteps: any[] = [];

  /**
   * Subject that manages the current state of contact details steps
   */
  private contactDetailsStepsSubject = new BehaviorSubject<number>(null);

  constructor(
    private api: ApiInterfaceService,
    private analyticsService: AnalyticsService,
    protected alertCtrl: AlertController,
    private accountService: AccountService,
    protected modalCtrl: ModalController,
    private navCtrl: NavController,
    private titleCasePipe: TitleCasePipe,
  ) {}

  /**
   * Updates the contact steps state.
   *
   * @param step - current contact details steps.
   */
  updateContactDetailsSteps(step: number): void {
    this.contactDetailsStepsSubject.next(step);
  }

  /**
   * Gets the current value of contact details steps
   */
  get currentContactSteps(): number {
    return this.contactDetailsStepsSubject.getValue();
  }

  /**
   * Check consumer profile status and show modal accordingly.
   *
   */
  async checkConsumerOnboardingStatus() {
    const consumerOnboardingStatus: ConsumerAccountStatus = await this.getConsumerAccountStatus();
    const consumerOnboardingData: ConsumerAccount = await this.getConsumerDetails('paymentDetails');

    const showBasicSuccessModal =
      consumerOnboardingStatus.completedAndVerified &&
      consumerOnboardingData.profile.paymentMethodCode !== 'credit_card' &&
      consumerOnboardingData.profile.fundingSourceCode !== 'ndis_plan_managed' &&
      consumerOnboardingData.profile.fundingSourceCode !== 'home_care_package';

    if (showBasicSuccessModal) {
      const alert = await this.alertCtrl.create({
        header: 'Success',
        message: 'Your account details have been successfully updated.',
        buttons: [{ text: 'Got it', role: 'cancel' }],
      });

      await alert.present();
    } else if (consumerOnboardingStatus.percentage === 100) {
      const modal = await this.modalCtrl.create({
        component: this.getCorrectModalComponentBasedOnFunding(consumerOnboardingData),
        componentProps: {
          hasAgreement: consumerOnboardingStatus.hasAgreement,
          fundingSourceCode: consumerOnboardingData.profile.fundingSourceCode,
          completedAndVerified: consumerOnboardingStatus.completedAndVerified,
        },
        cssClass: 'fullpage-modal',
      });

      // https://github.com/ionic-team/ionic-framework/issues/26872
      modal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

      await modal.present();
    }

    // show incomplete profile modal if user has not finished previous steps
    const status = [
      !consumerOnboardingStatus.status.personal,
      !consumerOnboardingStatus.status.mySupport,
      !consumerOnboardingStatus.status.payment,
    ].filter((value) => value);

    if (status.length > 0) {
      const hasMany: boolean = status.length > 1;
      const header = `The following ${hasMany ? 'steps are' : 'step is'} incomplete`;
      const message = `Please complete the following step${hasMany ? 's' : ''} to complete your account verification:
    <ul>${!consumerOnboardingStatus.status.personal ? '<li>Personal details</li>' : ''}${!consumerOnboardingStatus.status.mySupport ? '<li>My contacts</li>' : ''}${!consumerOnboardingStatus.status.payment ? '<li>Payment details</li>' : ''}</ul>`;

      const nextRoute = this.getNextOnboardingRoute(consumerOnboardingStatus.status);

      const alert = await this.alertCtrl.create({
        header,
        message,
        buttons: [
          {
            text: 'COMPLETE NOW',
            handler: () => {
              if (nextRoute) {
                this.navCtrl.navigateForward(nextRoute);
              }
            },
          },
        ],
      });

      await alert.present();
    }
  }

  /**
   * Checks if applied voucher is valid.
   *
   * @param voucherCode - Voucher code.
   *
   * @param useAuth - user credentials.
   */
  checkVoucherValidity(voucherCode: string, useAuth = false): Observable<VoucherAttributes> {
    return this.api
      .get$<VoucherResponse>('/api/v1/vouchers/validate_voucher.json', useAuth, {
        voucher_code: voucherCode,
      })
      .pipe(
        map((response) => response.data.attributes),
        catchError((error) => {
          console.error('Failed to retrieve voucher', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Redirect to specific path.
   *
   * @param path - Redirection path.
   * @param stepNumber - Step number.
   */
  redirectToSpecificPath(path: string, stepNumber?: number): void {
    if (stepNumber) {
      this.navCtrl.navigateForward(path, { queryParams: { currentStep: stepNumber } });
    } else {
      this.navCtrl.navigateForward(path);
    }
  }

  /**
   * Sends a request for fetching latest consumer account navigation steps status.
   *
   */
  async getConsumerAccountStatus(): Promise<ConsumerAccountStatus> {
    try {
      const res = await this.api.gqlQuery(gql`
        {
          clientOnboardingStatus {
            status {
              mySupport
              ndisPlan
              payment
              personal
            }
            percentage
            hasAgreement
            showVerificationMessage
            completedAndVerified
          }
        }
      `);

      return res['clientOnboardingStatus'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a request for fetching latest consumer details.
   *
   * @param componentName - Component name.
   */
  async getConsumerDetails(componentName): Promise<ConsumerAccount> {
    try {
      const res = await this.api.gqlQuery(gql`
        {
          user {
            ...User
          }
        }
        ${componentName === 'consumerNavigation' ? consumerNavigation : ''}
        ${componentName === 'personalDetails' ? personalDetails : ''}
        ${componentName === 'personReceivingSupport' ? personReceivingSupport : ''}
        ${componentName === 'supportTakePlace' ? supportTakePlace : ''}
        ${componentName === 'behaviourSupportPlan' ? behaviourSupportPlan : ''}
        ${componentName === 'personManagingAccount' ? personManagingAccount : ''}
        ${componentName === 'contacts' ? contacts : ''}
        ${componentName === 'paymentDetails' ? paymentDetails : ''}
        ${componentName === 'paymentFundingInfo' ? PaymentFundingInformation : ''}
        ${componentName === 'PaymentUploadAuthorisationForm' ? PaymentUploadAuthorisationForm : ''}
        ${componentName === 'paymentDebitCard' ? paymentDebitCard : ''}
        ${componentName === 'paymentCreditCard' ? paymentCreditCard : ''}
        ${componentName === 'paymentInvoice' ? PaymentInvoice : ''}
        ${componentName === 'paymentNdisnumber' ? PaymentNdisnumber : ''}
        ${componentName === 'paymentHomeCarePackageProvider' ? paymentHomeCarePackageProvider : ''}
        ${componentName === 'paymentHowToVerify' ? PaymentHowToVerify : ''}
        ${componentName === 'supportNeeds' ? supportNeeds : ''}
        ${componentName === 'about' ? about : ''}
        ${componentName === 'houseHoldDetails' ? houseHold : ''}
        ${componentName === 'petsDetails' ? petsDetails : ''}
        ${componentName === 'scheduleCall' ? ScheduleCall : ''}
        ${componentName === 'jobPosted' ? jobPosted : ''}
      `);

      return res['user'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a mutation to update consumer details.
   *
   * @param consumerUpdatedObject - Send the latest data to api.
   */
  async updateConsumerDetails(consumerUpdatedObject: ConsumerAccount) {
    try {
      const res = await this.api.gqlMutation(
        gql`
          mutation updateConsumerDetails($clientAttributes: ClientAttributes!) {
            clientUpdate(input: { userAttributes: $clientAttributes }) {
              user {
                id
              }
            }
          }
        `,
        { clientAttributes: consumerUpdatedObject },
      );

      // Send a Identify event if any core properties are being updated in the `consumerUpdatedObject`
      const identifyPayload = this.createIdentifyProperties(consumerUpdatedObject);
      if (Object.keys(identifyPayload).length) {
        this.analyticsService.identify(identifyPayload);
      }

      return res;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sets the payment details steps based on the user's onboarding data.
   *
   * @param consumerOnboardingData - Consumer onboarding details
   */
  setPaymentDetailsSteps(consumerOnboardingData: ConsumerAccount) {
    const profile = consumerOnboardingData.profile;
    let steps: Step[] = [];

    switch (profile.paymentMethodCode) {
      case 'credit_card':
        steps = this.getCreditCardSteps();
        break;
      case 'direct_debit':
        steps = this.getDirectDebitSteps(profile);
        break;
      case 'invoice':
        steps = this.getInvoiceSteps(profile);
        break;
      default:
        steps = [FUNDING_INFO_STEP];
    }

    this.paymentDetailsSteps = steps;
  }

  /**
   * Returns the steps for credit card payment method.
   */
  private getCreditCardSteps(): Step[] {
    return [cloneStep(PAYMENT_INFORMATION_STEP), cloneStep(ADD_CARD_STEP), cloneStep(RECEIPTS_STEP)];
  }

  /**
   * Returns the steps for direct debit payment method.
   *
   * @param profile - user profile
   */
  private getDirectDebitSteps(profile: ConsumerAccount['profile']): Step[] {
    const directDebitSteps: Step[] = [
      cloneStep(PAYMENT_INFORMATION_STEP),
      cloneStep(VERIFY_PAYMENT_STEP),
      cloneStep(DIRECT_DEBIT_STEP),
      cloneStep(AUTHORISATION_FORM_STEP),
      cloneStep(UPLOAD_AUTHORISATION_FORM_STEP),
    ];
    if (profile.coordinatorManaged || profile.participantRelationship !== 'Self') {
      directDebitSteps.push(cloneStep(POWER_OF_ATTORNEY_STEP));
    }
    directDebitSteps.push(cloneStep(RECEIPTS_STEP));
    return directDebitSteps;
  }

  /**
   * Returns the steps for invoice payment method.
   *
   * @param profile - user profile
   */
  private getInvoiceSteps(profile: ConsumerAccount['profile']): Step[] {
    const invoiceSteps: Step[] = [FUNDING_INFO_STEP];
    switch (profile.fundingSourceCode) {
      case 'ndis_self_managed':
        invoiceSteps.push(
          cloneStep(VERIFY_PAYMENT_STEP),
          cloneStep(UPLOAD_NDIS_LETTER_STEP),
          cloneStep(NDIS_NUMBER_STEP),
          cloneStep(INVOICING_STEP),
        );
        break;
      case 'ndis_plan_managed':
        if (!profile.assignPartnerFundingOrganisation && profile.organisationRequest) {
          invoiceSteps.push(cloneStep(ADD_ORGANIZATION_STEP), cloneStep(NDIS_NUMBER_STEP), cloneStep(INVOICING_STEP));
        } else {
          invoiceSteps.push(cloneStep(FUNDING_ORGANISATION_STEP), cloneStep(INVOICING_STEP));
        }
        break;
      case 'ndis_agency_managed':
      case 'other':
        invoiceSteps.push(cloneStep(VERIFY_PAYMENT_STEP), cloneStep(INVOICING_STEP));
        break;
      case 'home_care_package':
        invoiceSteps.push(cloneStep(HOME_CARE_PACKAGE_STEP), cloneStep(INVOICING_STEP));
        break;
    }
    return invoiceSteps;
  }

  /**
   * This function is to show dynamic alertbox
   *
   * @param header - text of header
   * @param message - text of message
   * @param buttonText - text of button
   */
  async openDynamicAlertBox(header: string, message: string, buttonText: string) {
    const alert = await this.alertCtrl.create({
      header,
      message,
      buttons: [
        {
          text: buttonText,
          handler: () => {
            this.alertCtrl.dismiss();
          },
        },
      ],
    });
    alert.present();
  }

  /*
   * Sends a request for fetching services data from api.
   *
   * @param {*} type - Services category.
   */
  async getServicesList(type: string): Promise<[Services]> {
    try {
      const res = await this.api.gqlQuery(gql`
          {
            services(code: ${type}) {
              id
              sortValue
              name
              dialog
            }
          }
        `);

      return res['services'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Requests the GQL server to validate a tokens fingerprint. Used to prevent
   * dummy credit cards being accepted in our production environment.
   *
   * @param token - The credit card token provided from Spreedly.
   * @returns A boolean, true if the token is valid in the environment, else false.
   */
  async isPaymentTokenFingerprintValid(token: string): Promise<boolean> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query validatePaymentToken($paymentMethodToken: String!) {
            validatePaymentMethod(paymentMethodToken: $paymentMethodToken) {
              paymentMethodValid
            }
          }
        `,
        {
          paymentMethodToken: token,
        },
      );
      return res['validatePaymentMethod']['paymentMethodValid'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Generates a payload for Identify to emit to Segment.
   *
   * @param consumerUpdatedObject - A consumerUpdatedObject object that may contain the fields identify is concerned about.
   * @returns A AnalyticsEventIdentifyProperties object.
   */
  private createIdentifyProperties(consumerUpdatedObject: ConsumerAccount): AnalyticsEventIdentifyProperties {
    let analyticsEventIdentifyProperties: AnalyticsEventIdentifyProperties = {};

    // Checks if funding information was changed, if so emit.
    // The Identify call uses a queue under the hood which will obscure a clients funding type.
    const fundingSourceCode = consumerUpdatedObject?.profile?.fundingSourceCode;
    if (fundingSourceCode) {
      analyticsEventIdentifyProperties.funding_type = consumerUpdatedObject?.profile?.fundingSourceCode;
    }
    const paymentMethodCode = consumerUpdatedObject?.profile?.paymentMethodCode;
    if (fundingSourceCode === '' && ['direct_debit', 'credit_card'].includes(paymentMethodCode)) {
      // Paying privately doesnt emit a value, must be inferred from the paymentMethodCode
      analyticsEventIdentifyProperties.funding_type = 'privately_paying';
    }

    // We only want to return a object containing the fields the consumer update object contains.
    // This way we dont override properties with null or empty strings
    if (consumerUpdatedObject.firstName)
      analyticsEventIdentifyProperties.first_name = this.titleCasePipe.transform(consumerUpdatedObject.firstName);
    if (consumerUpdatedObject.lastName)
      analyticsEventIdentifyProperties.last_name = this.titleCasePipe.transform(consumerUpdatedObject.lastName);
    if (consumerUpdatedObject.contactPhone)
      analyticsEventIdentifyProperties.phone = attemptLocalToInternationalMobileNumber(
        consumerUpdatedObject.contactPhone,
      );
    if (consumerUpdatedObject?.profile?.participantAttributes?.gender) {
      // 'P' = 'Prefer not to say', mapped to P so Braze can handle it
      const gender = consumerUpdatedObject.profile.participantAttributes.gender;
      analyticsEventIdentifyProperties.gender = gender === 'NotDisclosed' ? 'P' : gender;
    }

    if (consumerUpdatedObject.birthDate) {
      const date = moment(consumerUpdatedObject.birthDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      if (date) analyticsEventIdentifyProperties.birthday = date;
    }

    // We need to provide email and userType here to prevent duplicate calls within the identify queue.
    // Only add them to the list of properties if there actually are any from the event.
    analyticsEventIdentifyProperties = this.createDefaultPropertiesForIdentify(analyticsEventIdentifyProperties);
    return analyticsEventIdentifyProperties;
  }

  /**
   * Generates default properties a identify needs to have. If the properties are empty, append no default traits.
   *
   * @param properties - The Identify event as it currently stands.
   * @returns The new event properties.
   */
  private createDefaultPropertiesForIdentify(
    properties: AnalyticsEventIdentifyProperties,
  ): AnalyticsEventIdentifyProperties {
    let props: AnalyticsEventIdentifyProperties = {};
    const usersEmailAddress = this.accountService.getUser()?.custom?.email;
    if (Object.keys(properties).length && !!usersEmailAddress) {
      props = {
        ...props,
        email: usersEmailAddress,
      };
    }

    const userType = this.analyticsService.accountTypeMapper(this.accountService.getUserType());
    if (Object.keys(properties).length && !!userType) {
      props = {
        ...props,
        user_type: userType,
      };
    }

    return {
      ...properties,
      ...props,
    };
  }

  private getCorrectModalComponentBasedOnFunding(
    consumerOnboardingData: ConsumerAccount,
  ): typeof ContactUsComponent | typeof CreditCardConfirmationComponent | typeof AccountVerificationComponent {
    if (
      consumerOnboardingData.profile.fundingSourceCode === 'other' ||
      consumerOnboardingData.profile.fundingSourceCode === 'ndis_agency_managed'
    ) {
      return ContactUsComponent;
    }

    if (consumerOnboardingData.profile.paymentMethodCode === 'credit_card') {
      return CreditCardConfirmationComponent;
    }

    return AccountVerificationComponent;
  }

  /**
   * Determines the next onboarding step a user should complete.
   *
   * @param status - The current status of the consumer's onboarding process.
   */
  private getNextOnboardingRoute(status: Partial<NonNullable<ConsumerAccountStatus['status']>>): string {
    if (!status.personal) return '/client-account/personal-details';
    if (!status.mySupport) return '/client-account/contacts';
    if (!status.payment) return '/client-account/payment-details';
    return '';
  }
}
